import morphdom from 'morphdom';
import shortid from 'shortid';
import dragDropUtils from '../dragdrop/utils';
import canvasUtils from '../utils';
import textUtils from './textUtils';

const componentsWithoutClone = ['scroll-button','tab-heads','tabs-container'];

export default class Element {
  constructor(element, eventBus) {
    this.listeners = {};
    this.eventBus = eventBus;
    this.element = element;
    this.document = this.element.ownerDocument;
    this.childrenSelected = [];
    this.disableButtons = [];
    this.dataAttributes = {gpClasses: '', gpAnimationDuration: '', gpOverlay: ''};
    if (this.element.hasAttribute('data-gp-component-id')) {
      this.id = this.element.getAttribute('data-gp-component-id');
    } else if (canvasUtils.getComponentIdFromClass(element)) {
      this.id = canvasUtils.getComponentIdFromClass(element);
    } else {
      this.id = canvasUtils.uniqueId();
    }
    this.componentType = this.element.hasAttribute('data-gp-container') ? 'container' : this.element.getAttribute('data-gp-component');
    this.registerElement();
    if (this.element.ownerDocument.getElementById('page-container'))
      this.page = this.element.ownerDocument
        .getElementById('page-container')
        .getAttribute('data-gp-page-id');
  }

  jsonObject(strData) {
    try {
      return JSON.parse(strData);
    } catch (e) {
      return strData;
    }
  }

  registerElement() {
    this.draggable = this.element.hasAttribute('data-gp-dnd');
    this.disableStyles = this.element.hasAttribute('data-gp-disable-styles');
    this.textEditable = this.element.hasAttribute('data-gp-text');
    this.disableButtons = JSON.parse(this.element.getAttribute('data-gp-disable-buttons') || '[]');

    this.element.classList.add('gp-component');
    this.element.classList.add(`gp-component-id-${ this.id }`);
    this.element.setAttribute('data-gp-component-id', this.id);
    this.element.setAttribute('data-gp-component', this.componentType);
    if (this.element.hasAttribute('data-gp-text')) {
      [...this.element.children].forEach(child => {
        if (
          !child.getAttribute('class') ||
          !child.getAttribute('class').includes('gp-component-id-')
        ) {
          child.classList.add(`gp-component-id-${canvasUtils.uniqueId()}`);
        }
      });
    }
    this.type = 'Component';

    const dataAttrs = Object.keys(this.dataAttributes);
    Object.keys(this.element.dataset).forEach(dataKey => {
      if (dataAttrs.includes(dataKey)) {
        this.dataAttributes[dataKey] = this.element.dataset[dataKey];
      }
    });

    if (this.element.hasAttribute('data-gp-block')) {
      this.type = 'Block';
      this.isBlock = true;
      this.blockId = this.element.getAttribute('data-gp-block-id');
      this.blockData = this.jsonObject(this.element.getAttribute('data-gp-block-data'));
      if (this.element.hasAttribute('data-gp-block-name')) {
        this.blockName = this.element.getAttribute('data-gp-block-name');
      } else {
        this.blockName = null;
      }
    } else if (this.element.hasAttribute('data-gp-container')) {
      this.type = 'Container';
    } else if (this.element.hasAttribute('data-gp-text')) {
      this.childrenSelected = [
        canvasUtils.getComponentIdFromClass(
          this.getElement().querySelector(
            '[class*="gp-component-id-"]:not(.ql-editor)'
          )
        )
      ];
      this.type = 'Text';
    }
    if (this.element.hasAttribute('data-gp-style')) {
      this.styleEditable = true;
      const componentName = this.element.getAttribute('data-gp-component');
      if (componentName.match(/^(null|video-overlay|button sub-label|button main-label)$/)) {
        this.configComponent = this.getConfigComponent();
        if (this.configComponent) {
          this.configType = this.configComponent.getAttribute(
            'data-gp-component'
          );
          this.configId = this.configComponent.getAttribute(
            'data-gp-component-id'
          );
        }
      }
    }
    this.element.setAttribute('data-gp-component', this.componentType);
    canvasUtils.reflashComponentAttributes(this.element);
  }

  getElement() {
    return this.document.querySelector(`[data-gp-component-id="${this.id}"]`);
  }

  getConfigComponent(specificSelector = false) {
    const commonSelector =
      '[data-gp-component]:not([data-gp-component="null"]):not([data-gp-component="container"])';
    return this.element.parentNode.closest(specificSelector || commonSelector);
  }

  parentComponent() {
    if (this.isBlock) return false;
    const parentElement = this.element.parentNode.closest('.gp-component');
    if (parentElement)
      return parentElement.getAttribute('data-gp-component-id');
    return null;
  }

  updateContent(html) {
    this.eventBus.$emit('canvas-pre-change');
    morphdom(this.element, html);
    this.element.setAttribute('data-gp-component', this.componentType);
    this.registerElement();
    this.eventBus.$emit('canvas-change');
  }

  shouldShowClone() {
    if (this.element.id === 'groovemember-content-box') return false;
    if (componentsWithoutClone.includes(this.componentType)) return false;
    if (this.disableButtons.includes('clone')) return false;
    return true;
  }

  getMenu(extraItems) {
    const menu = document.createElement('div');
    menu.setAttribute('data-gp-menu-component-id', this.id);
    menu.classList.add('gp-component-menu');
    if (!this.isBlock && this.draggable) {
      const dragBtn = canvasUtils.createMenuButton({
        icon: 'icon-drag',
        title: 'Drag',
        callback: (e) => e.preventDefault()
      });
      dragBtn.addEventListener('mousedown', this.proxyDragStart.bind(this));
      dragBtn.addEventListener('mouseup', this.proxyDragStop.bind(this));
      dragBtn.classList.add('gp-component-drag-button');
      menu.appendChild(dragBtn);
    }

    extraItems.forEach(button => {
      menu.appendChild(button);
    });
    // check for groovemember
    if (this.shouldShowClone()) {
      const copyIcon = canvasUtils.createMenuButton({
        icon: 'icon-single-copy-04',
        title: 'Copy',
        callback: () => this.clone()
      });
      menu.appendChild(copyIcon);
    }


    // check for groovemember
    if (this.element.id !== 'groovemember-content-box') {
      const removeIcon = canvasUtils.createMenuButton({
        icon: 'icon-trash',
        title: 'Delete',
        callback: () => this.delete()
      });
      menu.appendChild(removeIcon);
    }


    if (this.parentComponent()) {
      const parentButton = canvasUtils.createMenuButton({
        icon: 'icon-move-layer-up',
        title: 'Move up',
        callback: () => this.jumpToParent()
      });
      menu.appendChild(parentButton);
    }

    // const helpBtn = canvasUtils.createMenuButton({icon: 'icon-question'});
    // menu.appendChild(helpBtn);

    if (!this.disableStyles && JSON.parse(localStorage.getItem('gp-instant-style-panel-disable'))) {
      const stylesBtn = canvasUtils.createMenuButton({
        icon: 'icon-ellipsis-h',
        title: 'Open styles panel',
        callback: () => this.eventBus.$emit('edit-styles')
      });

      menu.appendChild(stylesBtn);
    }

    return menu;
  }

  clone(silent) {
    this.eventBus.$emit('canvas-pre-change');
    const clone = this.element.cloneNode(true);

    if( this.element.getAttribute('data-gp-button-id') ){
      const cloneId = shortid.generate();
      clone.setAttribute('data-gp-button-id',cloneId);
      clone.innerHTML = this.element.innerHTML.replaceAll(this.element.getAttribute('data-gp-button-id'),cloneId);
    }

    if (clone.hasAttribute('data-gp-text')) textUtils.destroyEditor(clone);
    canvasUtils.cleanCanvas(clone);
    this.element.parentNode.insertBefore(clone, this.element.nextSibling);
    canvasUtils.componentCloned(clone, this.eventBus);
    this.eventBus.$emit('canvas-change');
    return clone.getAttribute('data-gp-component-id');
  }

  cloneTextAndMove(tag) {
    this.eventBus.$emit('canvas-pre-change');
    const clone = this.element.cloneNode(true);
    clone.innerHTML = `<${tag}>Insert your text here</${tag}>`;
    this.element.parentNode.insertBefore(clone, this.element.nextSibling);
    canvasUtils.componentCloned(clone, this.eventBus);
    this.eventBus.$emit('canvas-change');
    clone.click();
  }

  delete() {
    const confirmDelete = () => {
      const parentBlock = this.element.closest('[data-gp-block]');
      const isGlobalBlock = this.element.getAttribute('data-gp-block-type') === 'global_block';
      this.eventBus.$emit('canvas-pre-change');
      this.cleanParent(this.element.parentNode.closest('.gp-component'));
      this.element.remove();
      this.handleEmptyBlock(parentBlock);
      canvasUtils.triggerEvent(
        this.element.ownerDocument.defaultView,
        'resize'
      );
      this.eventBus.$emit('canvas-change');
      if (isGlobalBlock) this.eventBus.$emit('global-block-delete');
    };
    this.eventBus.$emit('action-request', { callback: confirmDelete });
  }

  handleEmptyBlock(block) {
    if (!block) return;
    const children = Array.from(block.children).filter(
      el => !el.matches('[data-section-overlay], [data-gp-block-anchor]')
    );
    if (!this.isBlock && !children.length) block.remove();
  }

  cleanParent(element) {
    if (element && !this.isBlock) {
      element.childNodes.forEach(node => {
        if (
          node.nodeType === Node.TEXT_NODE &&
          !/[^\t\n\r ]/.test(node.textContent)
        )
          node.remove();
      });
    }
  }

  moveUp(toReplace) {
    this.eventBus.$emit('canvas-pre-change');

    const clone = this.element.cloneNode(true);

    this.element.parentNode.insertBefore(clone, toReplace || this.element.previousElementSibling);

    this.element.remove();

    this.eventBus.$emit('canvas-change');
  }

  moveDown(toReplace) {
    this.eventBus.$emit('canvas-pre-change');

    const clone = toReplace.cloneNode(true);

    this.element.parentNode.insertBefore(clone, this.element);

    toReplace.remove();

    this.eventBus.$emit('canvas-change');
  }

  jumpToParent() {
    this.eventBus.$emit('select-element-request', this.parentComponent());
  }

  setTextPlaceholder() {
    if (this.textEditable && this.element.textContent.trim() === '') {
      this.element.querySelector(
        '[class*="gp-component-id-"]:not(.ql-editor)'
      ).innerHTML = 'ADD TEXT HERE.';
    }
  }

  editText() {
    if( !this.getElement().classList.contains('ql-container') ){
      this.eventBus.$emit('canvas-pre-change');
      this.eventBus.$emit('edit-text', {
        element: this
      });
      this.childrenSelected = [
        canvasUtils.getComponentIdFromClass(
          this.getElement().querySelector('[class*="gp-component-id-"]')
        )
      ];
      this.eventBus.$emit('canvas-change');
    }
  }

  replaceContent(content) {
    this.element.innerHTML = content;
    dragDropUtils.removeDragState(this.element);
  }

  getContent() {
    return this.element.innerHTML;
  }

  proxyDragStart() {
    this.document.body.classList.add('gp-drag-enabled');
    canvasUtils.triggerManualEvent(this.element, 'mousedown');
  }

  proxyDragStop() {
    canvasUtils.triggerManualEvent(this.element, 'mouseup');
    this.document.body.classList.remove('gp-drag-enabled');
  }
}
